import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import CgmModulePageLayout from './layout/CgmModulePageLayout';
import './CgmFilterToolbar.css';
import './CgmModulePage.css';
import {
  CGM_MONTH_OPTIONS,
  getCurrentMonthYear,
  filterByMonthYear,
  buildYearOptions,
} from '../utils/cgmMonthYearFilter';
import { authJsonHeaders } from '../utils/authHeaders';

const { month: DEFAULT_FILTER_MONTH, year: DEFAULT_FILTER_YEAR } = getCurrentMonthYear();

export default function CaisseCgmPage() {
  const { user, isCaissier, activeCabinet } = useAuth();
  const caisseLabel = activeCabinet?.is_default || Number(activeCabinet?.id) === 1
    ? 'Retrait CGM1'
    : 'Retrait CGM2';
  const [operations, setOperations] = useState([]);
  const [soldeActuel, setSoldeActuel] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const soldeFromLastOperation = React.useRef(null);
  const [filterMonth, setFilterMonth] = useState(DEFAULT_FILTER_MONTH);
  const [filterYear, setFilterYear] = useState(DEFAULT_FILTER_YEAR);
  const [searchQuery, setSearchQuery] = useState('');
  /** '' | 'bureau' | 'autre' */
  const [filterType, setFilterType] = useState('');

  // Formulaire pour ajouter/modifier une opération
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingOperationId, setEditingOperationId] = useState(null);
  const [editingSourceType, setEditingSourceType] = useState('');
  const [formData, setFormData] = useState({
    type_operation: 'retrait',
    montant: '',
    commentaire: '',
    client_username: '',
    operation_sign: 'moins',
    date_retrait: new Date().toISOString().slice(0, 10),
    retrait_type: 'autre', // inventaire | bureau | autre
  });

  // #region agent log
  const logEvent = (payload) => {
    fetch('http://127.0.0.1:7242/ingest/4c19617c-ffd9-4631-93bb-12be75be63ad', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sessionId: 'debug-session',
        runId: 'run1',
        ...payload,
        timestamp: Date.now()
      })
    }).catch(() => { });
  };
  // #endregion

  // Charger les opérations et le solde
  const fetchData = async () => {
    try {
      setLoading(true);
      setError(''); // Réinitialiser l'erreur

      const response = await fetch('/api/caisse-cgm', {
        headers: authJsonHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success) {
        setOperations(data.operations || []);
        const yearReste = data.reste_cgm_annee != null ? Number(data.reste_cgm_annee) : null;
        const serverSolde = yearReste != null && !isNaN(yearReste)
          ? yearReste
          : (data.solde_actuel != null ? Number(data.solde_actuel) : null);
        if (serverSolde !== null && !isNaN(serverSolde)) {
          setSoldeActuel(serverSolde);
          soldeFromLastOperation.current = null;
        }
      } else {
        setError(data.error || data.message || 'Erreur lors du chargement des données');
        console.error('Erreur API:', data);
      }
    } catch (err) {
      const errorMessage = err.message || 'Erreur de connexion au serveur';
      setError(errorMessage);
      console.error('Erreur fetch:', err);

      // Vérifier si le serveur backend est accessible
      if (err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
        setError('Impossible de se connecter au serveur. Vérifiez que le serveur backend est démarré.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Rafraîchir toutes les 1 minute (60 secondes)
    const interval = setInterval(fetchData, 60000);

    const handleCaisseUpdated = () => {
      fetchData();
    };

    window.addEventListener('caisse-updated', handleCaisseUpdated);
    window.addEventListener('focus', handleCaisseUpdated);

    return () => {
      clearInterval(interval);
      window.removeEventListener('caisse-updated', handleCaisseUpdated);
      window.removeEventListener('focus', handleCaisseUpdated);
    };
  }, [activeCabinet?.id]);

  // Gérer le changement dans le formulaire
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      // Pour le montant, garder la valeur telle quelle si elle est vide, sinon la convertir en nombre positif
      [name]: name === 'montant' ? (value === '' ? '' : Math.abs(parseFloat(value || 0))) : value
    }));
  };

  // Réinitialiser le formulaire
  const resetForm = () => {
    setFormData({
      type_operation: 'retrait',
      montant: '',
      commentaire: '',
      client_username: '',
      operation_sign: 'moins',
      date_retrait: new Date().toISOString().slice(0, 10),
      retrait_type: 'autre',
    });
    setIsEditing(false);
    setEditingOperationId(null);
    setEditingSourceType('');
  };

  const openFormFor = (type) => {
    // Pas de dépôt manuel : le solde = honoraires reçus − retraits (inventaire / fournitures bureau)
    if (type === 'depot') return;
    const today = new Date().toISOString().slice(0, 10);
    setIsEditing(false);
    setEditingOperationId(null);
    setEditingSourceType('');
    setFormData({
      type_operation: type,
      montant: '',
      commentaire: '',
      client_username: '',
      operation_sign: 'moins',
      date_retrait: today,
      retrait_type: 'autre',
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    if (showForm) {
      // #region agent log
      logEvent({
        hypothesisId: 'H1',
        location: 'CaisseCgmPage.js:showFormEffect',
        message: 'Form visible state changed',
        data: { showForm, type_operation: formData.type_operation }
      });
      // #endregion
    }
  }, [showForm, formData.type_operation]);

  const instrumentedOpenFormFor = (type) => {
    // #region agent log
    logEvent({
      hypothesisId: 'H2',
      location: 'CaisseCgmPage.js:openFormFor',
      message: 'Open form requested',
      data: { requestedType: type }
    });
    // #endregion
    openFormFor(type);
  };

  // Modifier une opération (pré-remplir le formulaire)
  const handleEditOperation = (operation) => {
    // Seules valeurs autorisées dans le sélecteur: 'retrait' | 'autre'
    const allowedTypes = ['retrait', 'autre', 'depot'];
    const normalizedType = allowedTypes.includes(operation.type_operation)
      ? operation.type_operation
      : 'retrait';

    // Extraire la date de l'opération
    let dateRetrait = new Date().toISOString().slice(0, 10);
    if (operation.date) {
      dateRetrait = String(operation.date).slice(0, 10);
    } else if (operation.created_at) {
      const d = new Date(operation.created_at);
      dateRetrait = !isNaN(d.getTime()) ? d.toISOString().slice(0, 10) : dateRetrait;
    }

    setFormData({
      type_operation: normalizedType,
      montant: Math.abs(parseFloat(operation.montant) || 0),
      commentaire: operation.commentaire || '',
      client_username: operation.client_username || '',
      operation_sign: operation.operation_sign || 'moins',
      date_retrait: dateRetrait,
      retrait_type: getRetraitTypeKey(operation),
    });
    setIsEditing(true);
    setEditingOperationId(operation.id);
    setEditingSourceType(operation.source_type || '');
    setShowForm(true);
    // Scroller vers le formulaire
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Supprimer une opération
  const handleDeleteOperation = async (operation) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette opération ? Cette action est irréversible.')) {
      return;
    }

    try {
      const operationId = typeof operation === 'object' ? operation.id : operation;
      const sourceType = typeof operation === 'object' ? (operation.source_type || '') : '';
      const qs = sourceType ? `?source_type=${encodeURIComponent(sourceType)}` : '';
      const response = await fetch(`/api/caisse-cgm/${operationId}${qs}`, {
        method: 'DELETE',
        headers: authJsonHeaders(),
      });

      // Gérer les réponses non-JSON (ex: 404 HTML si backend non redémarré)
      if (!response.ok) {
        const text = await response.text();
        let serverMessage = `Erreur serveur ${response.status}`;
        try {
          const parsed = JSON.parse(text);
          serverMessage = parsed.error || parsed.message || serverMessage;
        } catch (_) {
          if (text && !text.startsWith('<')) serverMessage = text.substring(0, 150);
        }
        setError(serverMessage);
        return;
      }

      const data = await response.json();

      if (data.success) {
        setSuccessMessage('Opération supprimée avec succès');
        fetchData();
        // Rafraîchir la caisse dans le header
        if (window.dispatchEvent) {
          window.dispatchEvent(new CustomEvent('caisse-updated'));
        }
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(data.error || data.message || 'Erreur lors de la suppression');
      }
    } catch (err) {
      const isNetworkError = !err.response && (err.message === 'Failed to fetch' || err.name === 'TypeError' || /network|networkerror/i.test(err.message));
      setError(isNetworkError ? 'Impossible de se connecter au serveur. Vérifiez que le backend est démarré (port 5000).' : (err.message || 'Erreur de connexion au serveur'));
      console.error('Erreur:', err);
    }
  };

  // Soumettre une nouvelle opération ou modifier une existante
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setLoading(true);

    // #region agent log
    logEvent({
      hypothesisId: 'H3',
      location: 'CaisseCgmPage.js:handleSubmit:start',
      message: 'Submit triggered',
      data: { type_operation: formData.type_operation, montant: formData.montant }
    });
    // #endregion

    // S'assurer que le montant est toujours positif
    const montantPositif = Math.abs(parseFloat(formData.montant || 0));

    if (!formData.montant || montantPositif <= 0) {
      setError('Le montant doit être supérieur à 0');
      setLoading(false);
      return;
    }

    if ((isEditing || formData.type_operation === 'retrait') && !(formData.commentaire || '').trim()) {
      setError('La note est obligatoire');
      setLoading(false);
      return;
    }

    try {
      // Nouveau flux Retrait : inventaire + cash_drop
      if (!isEditing && formData.type_operation === 'retrait') {
        const response = await fetch('/api/caisse-cgm/retrait', {
          method: 'POST',
          headers: authJsonHeaders(),
          body: JSON.stringify({
            montant: montantPositif,
            date: formData.date_retrait,
            solde_constate: soldeActuel,
            commentaire: formData.commentaire || null,
            retrait_type: formData.retrait_type || 'autre',
            type_retrait: formData.retrait_type || 'autre',
            is_bureau: formData.retrait_type === 'bureau' ? 1 : 0,
          }),
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok || !data.success) {
          setError(data.error || data.message || `Erreur serveur ${response.status}`);
          setLoading(false);
          return;
        }

        setSuccessMessage('Retrait enregistré (inventaire + cash drop)');
        resetForm();
        setShowForm(false);
        if (data.solde_actuel != null) {
          setSoldeActuel(Math.max(0, Number(data.solde_actuel)));
          soldeFromLastOperation.current = Math.max(0, Number(data.solde_actuel));
        }
        fetchData();
        window.dispatchEvent?.(new CustomEvent('caisse-updated'));
        setTimeout(() => setSuccessMessage(''), 3000);
        setLoading(false);
        return;
      }

      // Modification d'un cash_drop
      if (isEditing && (editingSourceType === 'cash_drop' || editingSourceType === 'cash_drops')) {
        const response = await fetch(`/api/caisse-cgm/${editingOperationId}`, {
          method: 'PUT',
          headers: authJsonHeaders(),
          body: JSON.stringify({
            source_type: 'cash_drop',
            montant: montantPositif,
            date: formData.date_retrait,
            date_retrait: formData.date_retrait,
            commentaire: formData.commentaire || null,
            note: formData.commentaire || null,
            type_operation: 'retrait',
            retrait_type: formData.retrait_type || 'autre',
            type_retrait: formData.retrait_type || 'autre',
            is_bureau: formData.retrait_type === 'bureau' ? 1 : 0,
          }),
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok || !data.success) {
          setError(data.error || data.message || `Erreur serveur ${response.status}`);
          setLoading(false);
          return;
        }

        setSuccessMessage('Retrait modifié avec succès');
        resetForm();
        setShowForm(false);
        if (data.solde_actuel != null) {
          setSoldeActuel(Math.max(0, Number(data.solde_actuel)));
          soldeFromLastOperation.current = Math.max(0, Number(data.solde_actuel));
        }
        fetchData();
        window.dispatchEvent?.(new CustomEvent('caisse-updated'));
        setTimeout(() => setSuccessMessage(''), 3000);
        setLoading(false);
        return;
      }

      // Autres types (dépôt…) : legacy désactivé — message d'erreur clair
      if (!isEditing && formData.type_operation !== 'retrait') {
        setError('Les dépôts / autres opérations legacy ne sont plus supportés. Utilisez un retrait via inventaire.');
        setLoading(false);
        return;
      }

      // Modification retrait (cash_drop) ou POST retrait de secours
      const url = isEditing
        ? `/api/caisse-cgm/${editingOperationId}`
        : '/api/caisse-cgm/retrait';

      const method = isEditing ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method: method,
        headers: authJsonHeaders(),
        body: JSON.stringify(
          isEditing
            ? {
                ...formData,
                montant: montantPositif,
                date: formData.date_retrait,
                date_retrait: formData.date_retrait,
                user_id: user?.id || null,
                source_type: editingSourceType || 'cash_drop',
                note: formData.commentaire || null,
                retrait_type: formData.retrait_type || 'autre',
                type_retrait: formData.retrait_type || 'autre',
                is_bureau: formData.retrait_type === 'bureau' ? 1 : 0,
              }
            : {
                montant: montantPositif,
                date: formData.date_retrait,
                date_retrait: formData.date_retrait,
                solde_constate: soldeActuel,
                commentaire: formData.commentaire || null,
                note: formData.commentaire || null,
                retrait_type: formData.retrait_type || 'autre',
                type_retrait: formData.retrait_type || 'autre',
                is_bureau: formData.retrait_type === 'bureau' ? 1 : 0,
              }
        )
      });

      if (!response.ok) {
        const text = await response.text();
        let serverMessage = `Erreur serveur ${response.status}`;
        try {
          const parsed = JSON.parse(text);
          serverMessage = parsed.error || parsed.message || serverMessage;
        } catch (_) {
          if (text && !text.startsWith('<')) serverMessage = text.substring(0, 150);
        }
        setError(serverMessage);
        setLoading(false);
        return;
      }

      const data = await response.json();

      if (data.success) {
        setSuccessMessage(isEditing ? 'Opération modifiée avec succès' : 'Opération enregistrée avec succès');
        resetForm();
        setShowForm(false);
        const newSolde = data.solde_actuel !== undefined && data.solde_actuel !== null ? Number(data.solde_actuel) : null;
        if (newSolde !== null) {
          setSoldeActuel(Math.max(0, newSolde));
          soldeFromLastOperation.current = Math.max(0, newSolde);
        }
        fetchData();
        if (window.dispatchEvent) {
          window.dispatchEvent(new CustomEvent('caisse-updated'));
        }
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(data.error || data.message || 'Erreur lors de l\'enregistrement');
      }
    } catch (err) {
      const isNetworkError = !err.response && (err.message === 'Failed to fetch' || err.name === 'TypeError' || /network|networkerror/i.test(err.message));
      setError(isNetworkError ? 'Impossible de se connecter au serveur. Vérifiez que le backend est démarré (port 5000).' : (err.message || 'Erreur de connexion au serveur'));
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatMontant = (montant) => {
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3
    }).format(montant);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const str = String(dateStr).trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
      const [y, m, d] = str.slice(0, 10).split('-');
      return `${d}/${m}/${y}`;
    }
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const yearOptions = useMemo(
    () => buildYearOptions(operations, (op) => op.date || op.created_at),
    [operations]
  );

  const getRetraitTypeKey = (op) => {
    const t = String(op?.type_retrait || op?.retrait_type || '').toLowerCase();
    if (t === 'bureau' || Number(op?.type_retrait) === 1 || op?.is_bureau) return 'bureau';
    if (t === 'inventaire') return 'inventaire';
    if (t === 'autre') return 'autre';
    if (op?.source_type === 'internal_expense_bureau') return 'bureau';
    if (op?.source_type === 'internal_expense_autre') return 'autre';
    return 'autre';
  };

  const typeOperationLabels = {
    retrait: 'Retrait',
    depot: 'Dépôt',
    paiement_client: 'Paiement Client',
    autre: 'Autre',
  };

  const filteredOperations = useMemo(() => {
    const q = String(searchQuery || '').trim().toLowerCase();
    // Recherche texte/montant : sur toutes les données ; sinon filtre mois/année
    let list = q
      ? [...operations]
      : filterByMonthYear(operations, filterMonth, filterYear, (op) => op.date || op.created_at);

    if (filterType) {
      list = list.filter((op) => getRetraitTypeKey(op) === filterType);
    }

    if (!q) return list;

    const qDigits = q.replace(/\s/g, '').replace(',', '.');
    return list.filter((op) => {
      const commentaire = String(op.commentaire || '').toLowerCase();
      const typeLabel = getRetraitTypeKey(op);
      const montantStr = String(op.montant ?? '').toLowerCase();
      const montantFmt = formatMontant(op.montant).toLowerCase().replace(/\s/g, '');
      const dateStr = formatDate(op.date || op.created_at).toLowerCase();
      const hay = `${commentaire} ${typeLabel} ${montantStr} ${dateStr}`;
      if (hay.includes(q)) return true;
      if (qDigits && (montantStr.includes(qDigits) || montantFmt.includes(qDigits.replace('.', ',')))) {
        return true;
      }
      return false;
    });
  }, [operations, filterMonth, filterYear, filterType, searchQuery]);

  const getTypeOperationLabel = (type) => typeOperationLabels[type] || type;

  const getTypeOperationColor = (type) => {
    const colors = {
      'retrait': 'danger',
      'depot': 'success',
      'paiement_client': 'warning',
      'autre': 'info'
    };
    return colors[type] || 'secondary';
  };

  if (loading && operations.length === 0) {
    return (
      <CgmModulePageLayout crumb="Comptabilité CGM · Caisse" backTo="/" wide className="caisse-page">
        <div className="text-center py-5">
          <div className="spinner-border" style={{ color: '#0f766e' }} role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
        </div>
      </CgmModulePageLayout>
    );
  }

  return (
    <CgmModulePageLayout crumb="Comptabilité CGM · Caisse" backTo="/" wide className="caisse-page">
      <style jsx global>{`
        body, html {
          background: #f8fafc !important;
        }
        .caisse-page {
          background: transparent;
          min-height: 0;
          padding: 0;
          font-family: 'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif;
          max-width: none;
          margin: 0;
        }
        .caisse-header {
          background: linear-gradient(135deg, #0f766e 0%, #115e59 55%, #134e4a 100%);
          color: #ffffff;
          padding: 1rem 1.25rem;
          border-radius: 12px;
          margin: 0 auto 1rem auto;
          max-width: 1100px;
          width: 100%;
          box-shadow: 0 8px 24px rgba(15, 118, 110, 0.28);
          border: 1px solid #99f6e4;
        }
        .caisse-header h2 {
          font-size: 1.25rem;
          font-weight: 700;
          margin: 0;
        }
        .caisse-solde-label {
          font-size: 0.72rem;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: #99f6e4;
          margin-bottom: 0.2rem;
        }
        .solde-display {
          font-size: 1.75rem;
          font-weight: 800;
          color: #fef08a;
          letter-spacing: 0.02em;
          font-variant-numeric: tabular-nums;
        }
        .caisse-live-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          font-size: 0.7rem;
          font-weight: 700;
          color: #166534;
          background: #dcfce7;
          padding: 0.15rem 0.45rem;
          border-radius: 999px;
          margin-left: 0.35rem;
          vertical-align: middle;
        }
        .caisse-actions {
          display: flex;
          gap: 0.5rem;
          justify-content: center;
          flex-wrap: wrap;
          margin-bottom: 1rem;
          max-width: 1100px;
          margin-left: auto;
          margin-right: auto;
        }
        .caisse-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.35rem;
          min-width: 7.5rem;
          padding: 0.45rem 1.1rem;
          border-radius: 8px;
          font-weight: 700;
          font-size: 0.85rem;
          border: 1px solid transparent;
          cursor: pointer;
          transition: all 0.18s ease;
        }
        .caisse-btn-retrait {
          background: linear-gradient(180deg, #ef4444 0%, #dc2626 100%);
          color: #ffffff;
          border-color: #b91c1c;
          box-shadow: 0 2px 8px rgba(220, 38, 38, 0.35);
        }
        .caisse-btn-retrait:hover {
          background: linear-gradient(180deg, #dc2626 0%, #b91c1c 100%);
          transform: translateY(-1px);
        }
        .caisse-retrait-type-row {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
        }
        .caisse-retrait-chip {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          padding: 0.45rem 0.9rem;
          border-radius: 999px;
          border: 1px solid #cbd5e1;
          background: #f8fafc;
          color: #334155;
          font-weight: 600;
          font-size: 0.9rem;
          cursor: pointer;
          user-select: none;
        }
        .caisse-retrait-chip input {
          margin: 0;
        }
        .caisse-retrait-chip.is-on {
          border-color: #0d9488;
          background: #ccfbf1;
          color: #5b21b6;
        }
        .caisse-btn-depot {
          background: linear-gradient(180deg, #22c55e 0%, #16a34a 100%);
          color: #ffffff;
          border-color: #15803d;
          box-shadow: 0 2px 8px rgba(22, 163, 74, 0.35);
        }
        .caisse-btn-depot:hover {
          background: linear-gradient(180deg, #16a34a 0%, #15803d 100%);
          transform: translateY(-1px);
        }
        .caisse-btn-ghost {
          background: #ffffff;
          color: #475569;
          border-color: #cbd5e1;
        }
        .caisse-page .alert {
          max-width: 1100px;
          margin-left: auto;
          margin-right: auto;
          border-radius: 10px;
        }
        
        @media (max-width: 768px) {
          .caisse-page .caisse-actions {
            margin-bottom: 0.6rem;
            gap: 0.35rem;
            padding: 0 0.15rem;
          }

          .caisse-page .caisse-btn {
            min-width: 0;
            flex: 1 1 0;
            max-width: none;
            padding: 0.35rem 0.55rem;
            font-size: 0.74rem;
            border-radius: 7px;
          }

          .caisse-page .caisse-btn:hover {
            transform: none;
          }

          .caisse-page .alert {
            margin-bottom: 0.6rem;
            padding: 0.45rem 0.65rem;
            font-size: 0.78rem;
          }
        }
        
        @media (max-width: 768px) {
          .caisse-header {
            padding: 0.75rem 1rem;
            margin: 0 auto 0.75rem auto;
          }
          
          .caisse-header .d-flex {
            flex-direction: column;
            align-items: flex-start !important;
            gap: 0.75rem;
          }
          
          .caisse-header .text-end {
            text-align: left !important;
            width: 100%;
          }
          
          .solde-display {
            font-size: 1.4rem;
          }
          
          .caisse-header h2 {
            font-size: 1.25rem;
            margin-bottom: 0.5rem !important;
          }
        }
        
        @media (max-width: 576px) {
          .caisse-header {
            padding: 0.6rem 0.75rem;
          }
          
          .solde-display {
            font-size: 1.2rem;
          }
          
          .caisse-header h2 {
            font-size: 1.1rem;
          }
        }
        
        .form-container {
          background: linear-gradient(180deg, #f0fdfa 0%, #ffffff 40%);
          border-radius: 12px;
          padding: 0;
          box-shadow: 0 8px 24px rgba(15, 118, 110, 0.1);
          border: 1px solid #99f6e4;
          max-width: 1100px;
          margin: 0 auto;
          width: 100%;
          overflow: hidden;
        }
        
        .modern-add-form {
          background: #ffffff;
          border-radius: 0;
          padding: 1.25rem;
          box-shadow: none;
          border: none;
        }
        
        .modern-add-form .modern-alert {
          border-radius: 12px;
          border: none;
          padding: 1rem 1.5rem;
          margin-bottom: 1rem;
          font-weight: 500;
        }
        
        .modern-add-form .modern-alert-danger {
          background: linear-gradient(45deg, #f8d7da, #f5c6cb);
          color: #721c24;
          border-left: 4px solid #dc3545;
        }
        
        .modern-add-form .modern-alert-success {
          background: linear-gradient(45deg, #d4edda, #c3e6cb);
          color: #155724;
          border-left: 4px solid #28a745;
        }
        
        .modern-add-form .modern-form-group {
          margin-bottom: 1rem;
        }
        
        .modern-add-form .modern-form-label {
          color: #2c3e50;
          font-weight: 600;
          margin-bottom: 0.4rem;
          display: block;
        }
        
        .modern-add-form .modern-form-input {
          width: 100%;
          border: 1px solid #d5dbe3;
          border-radius: 8px;
          padding: 10px 12px;
          font-size: 0.95rem;
          transition: all 0.2s ease;
          background: #ffffff;
        }
        
        .modern-add-form .modern-form-input:focus {
          outline: none;
          border-color: #0f766e;
          background: white;
          box-shadow: 0 0 0 3px rgba(15, 118, 110, 0.2);
        }
        
        .modern-add-form .modern-form-buttons {
          display: flex;
          gap: 1rem;
          justify-content: center;
          margin-top: 1rem;
        }
        
        .modern-add-form .modern-form-btn {
          border-radius: 12px;
          padding: 12px 22px;
          font-weight: 700;
          transition: all 0.2s ease;
          border: none;
          min-width: 120px;
          position: relative;
          overflow: hidden;
        }
        
        .modern-add-form .modern-form-btn::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
          transition: left 0.5s;
        }
        
        .modern-add-form .modern-form-btn:hover::before {
          left: 100%;
        }
        
        .modern-add-form .modern-form-btn-secondary {
          background: linear-gradient(45deg, #6c757d, #495057);
          color: white;
        }
        
        .modern-add-form .modern-form-btn-secondary:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 18px rgba(108, 117, 125, 0.28);
        }
        
        .modern-add-form .modern-form-btn-success {
          background: linear-gradient(180deg, #0f766e 0%, #115e59 100%);
          color: white;
        }
        
        .modern-add-form .modern-form-btn-success:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(15, 118, 110, 0.4);
        }
        
        .modern-add-form .modern-form-btn:disabled {
          opacity: 0.7;
          transform: none;
          box-shadow: none;
        }
        
        .modern-add-form .modern-spinner {
          width: 18px;
          height: 18px;
          border: 2px solid #f3f3f3;
          border-top: 2px solid #0f766e;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          display: inline-block;
          margin-right: 8px;
        }
        /* Overlay pour la modale Retrait / Dépôt */
        .caisse-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.55);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1.5rem;
          z-index: 1050;
        }
        .caisse-modal-card {
          max-width: 420px;
          width: 100%;
          max-height: 90vh;
          overflow: auto;
          border-radius: 12px;
          box-shadow: 0 15px 35px rgba(0,0,0,0.25);
        }
        .caisse-modal-card.form-container {
          max-width: 420px;
        }
        .caisse-modal-card .modern-add-form {
          padding: 1rem;
        }
        .caisse-form-stack {
          display: flex;
          flex-direction: column;
          gap: 0;
        }
        .caisse-form-stack .modern-form-group {
          margin-bottom: 0.75rem;
        }
        .caisse-form-stack .modern-form-group:last-child {
          margin-bottom: 0;
        }
        .caisse-form-stack .modern-form-hint {
          color: #64748b;
          font-size: 0.78rem;
          margin-top: 0.25rem;
          display: block;
        }
        .caisse-modal-card .modern-form-buttons {
          margin-top: 0.85rem;
          gap: 0.5rem;
        }
        .caisse-modal-card .modern-form-btn {
          padding: 0.5rem 1rem;
          font-size: 0.85rem;
          min-width: 100px;
          border-radius: 8px;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

      {/* En-tête avec solde */}


      {/* Messages */}
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          <i className="fas fa-exclamation-circle me-2"></i>
          {error}
          <button type="button" className="btn-close" onClick={() => setError('')}></button>
        </div>
      )}

      {successMessage && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          <i className="fas fa-check-circle me-2"></i>
          {successMessage}
          <button type="button" className="btn-close" onClick={() => setSuccessMessage('')}></button>
        </div>
      )}

      {/* Accès rapide : Retrait */}
      <div className="caisse-actions">
        <button type="button" className="caisse-btn caisse-btn-retrait" onClick={() => instrumentedOpenFormFor('retrait')}>
          <i className="fas fa-minus" aria-hidden="true" /> Retrait
        </button>
        {showForm && (
          <button type="button" className="caisse-btn caisse-btn-ghost" onClick={() => setShowForm(false)}>
            <i className="fas fa-times" aria-hidden="true" /> Fermer
          </button>
        )}
      </div>

      {/* Formulaire moderne - affiché en modal avec arrière-plan transparent */}
      {showForm && (
        <div className="caisse-modal-overlay" onClick={() => setShowForm(false)}>
          <div className="caisse-modal-card form-container mb-4" onClick={(e) => e.stopPropagation()}>
            <div className="modern-add-form">
              {error && (
                <div className="modern-alert modern-alert-danger">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  {error}
                </div>
              )}

              {successMessage && (
                <div className="modern-alert modern-alert-success">
                  <i className="fas fa-check-circle me-2"></i>
                  {successMessage}
                </div>
              )}

              {/* En-tête du formulaire */}
              <div style={{
                background: 'linear-gradient(135deg, #a71d2a 0%, #dc3545 100%)',
                color: 'white',
                padding: '0.75rem 1rem',
                borderRadius: '12px 12px 0 0',
                margin: '-1rem -1rem 1rem -1rem',
                fontWeight: '750',
                fontSize: '1rem',
                display: 'flex',
                alignItems: 'center'
              }}>
                <i className={`fas fa-${isEditing ? 'edit' : 'minus-circle'} me-2`}></i>
                {isEditing
                  ? 'Modifier l\'Opération'
                  : 'Retrait caisse'}
              </div>

              <form onSubmit={handleSubmit}>
                {(formData.type_operation === 'retrait' || isEditing) && (
                  <div className="caisse-form-stack">
                    {!isEditing && formData.type_operation === 'retrait' && (
                      <div className="modern-alert modern-alert-success" style={{ marginBottom: '0.75rem' }}>
                        Solde disponible : <strong>{formatMontant(soldeActuel)}</strong>
                      </div>
                    )}
                    <div className="modern-form-group">
                      <label className="modern-form-label">Type de retrait *</label>
                      <select
                        className="modern-form-input"
                        name="retrait_type"
                        value={['inventaire', 'bureau', 'autre'].includes(formData.retrait_type) ? formData.retrait_type : 'autre'}
                        onChange={(e) => setFormData((prev) => ({ ...prev, retrait_type: e.target.value }))}
                        required
                      >
                        <option value="inventaire">Inventaire</option>
                        <option value="bureau">Bureau</option>
                        <option value="autre">Autre</option>
                      </select>
                    </div>
                    <div className="modern-form-group">
                      <label className="modern-form-label">
                        <i className="fas fa-calendar me-1"></i>
                        Date *
                      </label>
                      <input
                        type="date"
                        className="modern-form-input"
                        name="date_retrait"
                        value={formData.date_retrait || ''}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="modern-form-group">
                      <label className="modern-form-label">
                        <i className="fas fa-money-bill me-1"></i>
                        Montant à retirer *
                      </label>
                      <input
                        type="number"
                        className="modern-form-input"
                        name="montant"
                        value={formData.montant || ''}
                        onChange={handleInputChange}
                        step="0.001"
                        min="0.001"
                        max={!isEditing ? soldeActuel || undefined : undefined}
                        required
                        placeholder="0,000"
                        onKeyDown={(e) => {
                          if (e.key === '-' || e.key === 'e' || e.key === 'E') {
                            e.preventDefault();
                          }
                        }}
                      />
                    </div>
                    <div className="modern-form-group">
                      <label className="modern-form-label">
                        <i className="fas fa-comment me-1"></i>
                        Note *
                      </label>
                      <input
                        type="text"
                        className="modern-form-input"
                        name="commentaire"
                        value={formData.commentaire || ''}
                        onChange={handleInputChange}
                        placeholder=""
                        required
                      />
                    </div>
                  </div>
                )}

                <div className="modern-form-buttons">
                  <button
                    type="button"
                    className="modern-form-btn modern-form-btn-secondary"
                    onClick={() => setShowForm(false)}
                  >
                    <i className="fas fa-times me-1"></i>
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="modern-form-btn modern-form-btn-success"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="modern-spinner"></span>
                        Enregistrement...
                      </>
                    ) : (
                      <>
                        <i className={`fas fa-${isEditing ? 'edit' : 'save'} me-1`}></i>
                        {isEditing ? 'Modifier' : 'Confirmer le retrait'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Liste des opérations - Tableau moderne */}
      <div className="form-container">
        <style>{`
          .modern-table-container {
            background: #ffffff;
            border-radius: 12px;
            padding: 0;
            box-shadow: 0 8px 24px rgba(15, 118, 110, 0.1);
            border: 1px solid #99f6e4;
            overflow: hidden;
          }
          
        .modern-table-header {
          background: #0f766e;
          color: #ffffff;
          padding: 0.65rem 0.85rem;
          font-weight: 700;
          font-size: 0.82rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
          border-bottom: 1px solid #115e59;
          gap: 10px;
          flex-wrap: wrap;
        }
        .caisse-month-year-filters {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }
        .caisse-month-year-filters label {
          color: #ffffff;
          font-weight: 700;
          font-size: 0.75rem;
          white-space: nowrap;
        }
        .caisse-month-year-select {
          border: none;
          border-radius: 8px;
          padding: 0.35rem 0.5rem;
          font-size: 0.78rem;
          font-weight: 600;
          color: #115e59;
          background: #ffffff;
          min-width: 7rem;
          cursor: pointer;
        }
        .caisse-month-year-select--year {
          min-width: 5.5rem;
        }
          
          .modern-table-header .header-title {
            display: flex;
            align-items: center;
            gap: 10px;
            flex-shrink: 0;
          }
          
          .modern-table-header .header-filters-scroll {
            display: flex;
            align-items: center;
            min-width: 0;
            flex: 1;
            overflow-x: auto;
            overflow-y: hidden;
            padding-bottom: 2px;
            -webkit-overflow-scrolling: touch;
          }
          
          .modern-table-header .header-filters-scroll::-webkit-scrollbar {
            height: 6px;
          }
          
          .modern-table-header .header-filters-scroll::-webkit-scrollbar-thumb {
            background: rgba(255, 255, 255, 0.4);
            border-radius: 3px;
          }
          
          .modern-table-header .header-filters-actions {
            display: flex;
            align-items: center;
            gap: 8px;
            flex-shrink: 0;
          }
          
          .modern-table-header .header-date-input {
            max-width: 130px;
            width: 130px;
            padding: 4px 6px !important;
            font-size: 0.875rem;
          }
          
          .modern-table-header .header-icon {
            color: #ffffff;
          }
          
          .modern-table-header .counter {
            background: rgba(255, 255, 255, 0.2);
            border: 1px solid rgba(255, 255, 255, 0.35);
            color: #ffffff;
            padding: 4px 12px;
            border-radius: 999px;
            font-size: 0.9rem;
            font-weight: 700;
          }
          
          .modern-table-wrapper {
            padding: 0;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }
          
          @media (max-width: 768px) {
            .modern-table {
              font-size: 0.85rem;
            }
            
            .modern-table thead th,
            .modern-table tbody td {
              padding: 0.5rem 0.4rem;
              white-space: nowrap;
            }
            
            .caisse-page .modern-table .btn-modifier-client,
            .caisse-page .modern-table .btn-supprimer-hono {
              width: 30px;
              height: 30px;
              min-width: 30px;
            }

            .caisse-page .modern-table .btn-modifier-client svg,
            .caisse-page .modern-table .btn-supprimer-hono svg {
              width: 15px !important;
              height: 15px !important;
            }
          }
          
          @media (max-width: 576px) {
            .modern-table {
              font-size: 0.75rem;
            }
            
            .modern-table thead th,
            .modern-table tbody td {
              padding: 0.4rem 0.3rem;
            }
            
            .caisse-page .modern-table .btn-modifier-client,
            .caisse-page .modern-table .btn-supprimer-hono {
              width: 28px;
              height: 28px;
              min-width: 28px;
            }

            .caisse-page .modern-table .btn-modifier-client svg,
            .caisse-page .modern-table .btn-supprimer-hono svg {
              width: 14px !important;
              height: 14px !important;
            }
            
            .modern-type-badge {
              padding: 4px 10px;
              font-size: 0.75rem;
            }
          }
          
          .modern-table {
            width: 100%;
            border-collapse: separate;
            border-spacing: 0;
            font-size: 0.95rem;
            border: 1px solid #d5dbe3;
          }
          
          .modern-table thead th {
            background: #0f766e;
            color: #ffffff;
            font-weight: 700;
            padding: 0.6rem 0.75rem;
            font-size: 0.8rem;
            text-align: left;
            border-bottom: 1px solid #115e59;
            border-right: 1px solid #e2e8f0;
            white-space: nowrap;
          }
          
          .modern-table thead th:last-child {
            border-right: none;
          }
          
          .modern-table tbody td {
            padding: 0.75rem;
            border-bottom: 1px solid #e3e7ee;
            border-right: 1px solid #e3e7ee;
            vertical-align: middle;
          }
          
          .modern-table tbody td:last-child {
            border-right: none;
          }
          
          .modern-table tbody tr {
            background: #ffffff;
            transition: all 0.2s ease;
          }
          
          .modern-table tbody tr:hover {
            background: #f0fdfa;
          }
          
          .modern-table tbody tr:last-child td {
            border-bottom: none;
          }
          
          .modern-table tfoot td {
            background: #f0fdfa;
            color: #1e40af;
            font-weight: 700;
            padding: 0.6rem 0.75rem;
            border-top: 1px solid #bfdbfe;
            border-right: 1px solid #e2e8f0;
          }
          
          .modern-table tfoot td:last-child {
            border-right: none;
          }
          
          .modern-type-badge {
            display: inline-block;
            padding: 6px 14px;
            border-radius: 20px;
            font-weight: 600;
            font-size: 0.85rem;
            white-space: nowrap;
          }
          
          .modern-type-badge.retrait {
            background: #fee2e2;
            color: #b91c1c;
            border: 1px solid #fecaca;
          }
          
          .modern-type-badge.depot {
            background: #dcfce7;
            color: #166534;
            border: 1px solid #bbf7d0;
          }
          
          .modern-type-badge.paiement {
            background: #fef3c7;
            color: #92400e;
            border: 1px solid #fde68a;
          }
          
          .modern-type-badge.autre {
            background: #dbeafe;
            color: #1e40af;
            border: 1px solid #bfdbfe;
          }
          
          .caisse-row-actions {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 6px;
            flex-wrap: nowrap;
          }

          .caisse-page .modern-table .btn-modifier-client {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 34px;
            height: 34px;
            background: #ffffff;
            color: #2563eb;
            border: 1px solid #dbeafe;
            border-radius: 8px;
            padding: 0;
            cursor: pointer;
            transition: background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
          }

          .caisse-page .modern-table .btn-modifier-client:hover {
            background: #f0fdfa;
            color: #0f766e;
            box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.16);
          }

          .caisse-page .modern-table .btn-supprimer-hono {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 34px;
            height: 34px;
            background: #ffffff;
            color: #dc2626;
            border: 1px solid #fee2e2;
            border-radius: 8px;
            padding: 0;
            cursor: pointer;
            transition: background 0.2s ease, color 0.2s ease;
          }

          .caisse-page .modern-table .btn-supprimer-hono:hover:not(:disabled) {
            background: #fef2f2;
            color: #b91c1c;
          }

          .caisse-page .modern-table .btn-modifier-client svg,
          .caisse-page .modern-table .btn-supprimer-hono svg {
            width: 18px;
            height: 18px;
            flex-shrink: 0;
            display: block;
          }
          
          .modern-amount {
            font-weight: 700;
            font-variant-numeric: tabular-nums;
          }
          
          .modern-amount.positive {
            color: #059669;
          }
          
          .modern-amount.negative {
            color: #dc2626;
          }
          
          .modern-empty-state {
            text-align: center;
            padding: 3rem 1rem;
            color: #6c757d;
          }
          
          .modern-empty-state i {
            font-size: 3rem;
            margin-bottom: 1rem;
            opacity: 0.5;
          }
        `}</style>

        <div className="modern-table-container">
          <div className="modern-table-header">
            <div className="header-title">
              <i className="fas fa-history header-icon"></i>
            </div>

            <div className="header-filters-scroll" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <div className="caisse-month-year-filters">
                <label htmlFor="caisse-filter-month">Mois / Année</label>
                <select
                  id="caisse-filter-month"
                  className="caisse-month-year-select"
                  value={filterMonth}
                  onChange={(e) => setFilterMonth(parseInt(e.target.value, 10))}
                >
                  {CGM_MONTH_OPTIONS.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}

                </select>

                <select
                  id="caisse-filter-year"
                  className="caisse-month-year-select caisse-month-year-select--year"
                  value={filterYear}
                  onChange={(e) => setFilterYear(parseInt(e.target.value, 10))}
                >
                  {yearOptions.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>

              <div className="caisse-month-year-filters">
                <label htmlFor="caisse-filter-type">Type</label>
                <select
                  id="caisse-filter-type"
                  className="caisse-month-year-select"
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  <option value="">Tous</option>
                  <option value="inventaire">Inventaire</option>
                  <option value="bureau">Bureau</option>
                  <option value="autre">Autre</option>
                </select>
              </div>

              <div className="caisse-month-year-filters" style={{ flex: '1 1 220px', minWidth: '180px' }}>
                <label htmlFor="caisse-filter-search">Recherche</label>
                <input
                  id="caisse-filter-search"
                  type="search"
                  className="caisse-month-year-select"
                  style={{ minWidth: '180px', width: '100%' }}
                  placeholder="Texte, commentaire, montant…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  aria-label="Rechercher par texte, commentaire ou montant"
                />
              </div>
            </div>

            <div className="header-filters-actions">
              <span className="counter">{filteredOperations.length}{operations.length ? ` / ${operations.length}` : ''}</span>
            </div>
          </div>

          <div className="modern-table-wrapper">
            {operations.length === 0 ? (
              <div className="modern-empty-state">
                <i className="fas fa-inbox"></i>
                <p>Aucune opération enregistrée</p>
              </div>
            ) : filteredOperations.length === 0 ? (
              <div className="modern-empty-state">
                <i className="fas fa-calendar-times"></i>
                <p>
                  {searchQuery.trim() || filterType
                    ? 'Aucun résultat pour cette recherche / ce filtre'
                    : (
                      <>
                        Aucune opération pour{' '}
                        {CGM_MONTH_OPTIONS.find((m) => m.value === filterMonth)?.label} {filterYear}
                      </>
                    )}
                </p>
              </div>
            ) : (
              <table className="modern-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Montant</th>
                    <th>Commentaire</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOperations.map((op) => {
                    const isPositive = op.type_operation === 'depot' || (op.type_operation === 'autre' && op.operation_sign === 'plus');
                    const typeClass = op.type_operation === 'paiement_client' ? 'paiement' : op.type_operation;

                    return (
                      <tr key={op.id}>
                        <td style={{ whiteSpace: 'nowrap' }}>{formatDate(op.date || op.created_at)}</td>
                        <td>
                          {(() => {
                            const isRetrait = op.type_operation === 'retrait' || op.source_type === 'cash_drop' || op.is_internal_expense || (op.source_type && op.source_type.startsWith('internal_expense'));
                            if (isRetrait) {
                              const k = getRetraitTypeKey(op);
                              const badgeClass = k === 'bureau' ? 'paiement' : (k === 'inventaire' ? 'retrait' : 'autre');
                              const label = k === 'bureau' ? 'Bureau' : (k === 'inventaire' ? 'Inventaire' : 'Autre');
                              return (
                                <span className={`modern-type-badge ${badgeClass}`} title={label}>
                                  {label}
                                </span>
                              );
                            }
                            return (
                              <span className={`modern-type-badge ${typeClass}`}>
                                {getTypeOperationLabel(op.type_operation)}
                              </span>
                            );
                          })()}
                        </td>
                        <td className={`modern-amount ${isPositive ? 'positive' : 'negative'}`}>
                          {isPositive ? '+' : '-'}
                          {formatMontant(op.montant)}
                        </td>
                        <td style={{
                          maxWidth: '400px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }} title={op.commentaire || ''}>
                          {op.commentaire || '-'}
                        </td>
                        <td>
                          {op.source_type === 'depense_cgm' ? (
                            <span style={{
                              color: '#6c757d',
                              fontStyle: 'italic',
                              fontSize: '0.85rem'
                            }}>
                              <i className="fas fa-info-circle"></i> Dépense CGM
                            </span>
                          ) : (
                            !isCaissier() && (
                              <div className="caisse-row-actions">
                                <button
                                  type="button"
                                  className="btn-modifier-client"
                                  onClick={() => handleEditOperation(op)}
                                  title="Modifier"
                                  aria-label="Modifier"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zm14.71-9.04c.39-.39.39-1.02 0-1.41l-2.5-2.5a.9959.9959 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.99-1.67z" />
                                  </svg>
                                </button>
                                <button
                                  type="button"
                                  className="btn-supprimer-hono"
                                  onClick={() => handleDeleteOperation(op)}
                                  title="Supprimer"
                                  aria-label="Supprimer"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                                  </svg>
                                </button>
                              </div>
                            )
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </CgmModulePageLayout>
  );
}

